/**
 * 测试报告页面：报告列表
 */
import { useCallback, useEffect, useState } from 'react'
import {
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Input,
  Drawer,
  Progress,
  Popconfirm,
  Select,
  Spin,
  Image,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { useSearchParams } from 'react-router-dom'
import '../../styles/aurora-page.css'
import '../../styles/aurora-modal.css'
import * as reportApi from '@/api/reports'
import type { TestReport, TestReportStep } from '@/api/reports'
import { useThemeStore } from '@/stores/theme'
import { useProjectStore } from '@/stores/project'
import { StepResultDetail, UIStepResultDetail } from './StepDetail'
import dayjs from 'dayjs'

const { Title } = Typography

/** 任务类型 */
const taskTypeText: Record<number, string> = { 1: '接口场景测试', 2: 'UI测试' }
const taskTypeColor: Record<number, string> = { 1: 'blue', 2: 'purple' }

/** 运行结果 */
const runResultText: Record<number, string> = { 1: '成功', 2: '失败' }

/** 运行模式 */
const runModeText: Record<number, string> = { 1: '同步', 2: '异步', 3: '定时器' }

/** 步骤类型 */
const stepTypeText: Record<number, string> = {
  1: 'HTTP请求', 2: '等待控制', 3: '条件控制', 4: '循环控制', 5: '断言控制', 6: '数据库', 7: '脚本',
  8: 'UI操作', 9: 'AI步骤', 10: 'UI断言',
}

/** 步骤执行状态 */
const reportStepStatusText: Record<number, string> = { 1: '成功', 2: '失败', 3: '错误', 4: '跳过' }
const reportStepStatusColor: Record<number, string> = { 1: 'success', 2: 'error', 3: 'warning', 4: 'default' }

/** 耗时格式化：ms -> 可读文本 */
const formatDuration = (ms: number) => {
  if (!ms || ms <= 0) return '-'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60000)}m${Math.round((ms % 60000) / 1000)}s`
}

/** 详情页统计行：label 居左、数值居右（基线对齐），参考 aurora-report.html 的 stat-row */
function StatRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, fontSize: 14 }}>
      <span style={{ color: 'var(--au-slate-muted)', whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ fontWeight: 800, color: 'var(--au-slate)', textAlign: 'right', wordBreak: 'break-all' }}>{children}</span>
    </div>
  )
}

/** 统计卡片样式（白底 + 柔和阴影，暗色模式由 CSS 变量自动适配） */
const statCardStyle: React.CSSProperties = {
  padding: 18,
  borderRadius: 12,
  background: 'var(--au-surface)',
  border: '1px solid var(--au-border)',
  boxShadow: 'var(--au-shadow-soft)',
}

/** 统计卡片标题样式 */
const statCardTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--au-slate-muted)',
  letterSpacing: '0.04em',
  marginBottom: 14,
}

export default function Reports() {
  const { isDark } = useThemeStore()
  const { currentProject } = useProjectStore()
  // 项目隔离：只查看当前项目的测试报告
  const projectId = currentProject?.id || 0
  const [searchParams] = useSearchParams()
  const [reports, setReports] = useState<TestReport[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [keyword, setKeyword] = useState('')
  // 列表筛选：任务类型 / 运行结果 / 运行模式（0=全部）
  const [taskTypeFilter, setTaskTypeFilter] = useState(0)
  const [runResultFilter, setRunResultFilter] = useState(0)
  const [runModeFilter, setRunModeFilter] = useState(0)
  const [loading, setLoading] = useState(false)
  const [viewing, setViewing] = useState<TestReport | null>(null)
  const [viewOpen, setViewOpen] = useState(false)
  // 四期 L：AI 摘要 / 失败分析 / 一键提单
  const [aiLoading, setAiLoading] = useState(false)
  const [analysisLoading, setAnalysisLoading] = useState(false)
  const [analysis, setAnalysis] = useState<reportApi.ReportAnalysis | null>(null)
  const [ticketLoading, setTicketLoading] = useState(false)
  // 四期 K：报告分享
  const [shareLoading, setShareLoading] = useState(false)

  const loadReports = useCallback(async (p = page, ps = pageSize, kw = keyword) => {
    setLoading(true)
    try {
      const res = await reportApi.listReports({
        page: p, page_size: ps, keyword: kw || undefined,
        task_type: taskTypeFilter || undefined,
        run_result: runResultFilter || undefined,
        run_mode: runModeFilter || undefined,
        project_id: projectId || undefined,
      })
      setReports(res.data || [])
      setTotal(res.total)
    } catch {
      setReports([])
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, keyword, taskTypeFilter, runResultFilter, runModeFilter, projectId])

  useEffect(() => {
    void loadReports()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, taskTypeFilter, runResultFilter, runModeFilter, projectId])

  // 切换项目时回到第一页，避免停留在超出新项目总量的页码
  useEffect(() => {
    setPage(1)
  }, [projectId])

  // 支持外部跳转携带 ?id=：进入页面后自动打开对应报告详情（执行中心「查看报告」跳入）。
  // 项目隔离：报告不属于当前项目时静默忽略
  useEffect(() => {
    const id = Number(searchParams.get('id'))
    if (!id) return
    ;(async () => {
      try {
        const detail = await reportApi.getReport(id)
        if (currentProject && detail.project_id && detail.project_id !== currentProject.id) return
        setViewing(detail)
        setViewOpen(true)
      } catch {
        /* 报告不存在时静默忽略（如已删除） */
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const handleSearch = () => {
    setPage(1)
    void loadReports(1, pageSize, keyword)
  }

  const handleView = async (record: TestReport) => {
    setViewing(record)
    setViewOpen(true)
    setAnalysis(null) // 四期 L：切换报告时重置失败分析缓存
    // 拉取完整详情（含步骤执行记录）
    try {
      const detail = await reportApi.getReport(record.id)
      setViewing(detail)
    } catch {
      /* 详情加载失败时保留列表数据展示 */
    }
  }

  // 四期 L：生成/重新生成 AI 摘要
  const handleGenSummary = async () => {
    if (!viewing) return
    setAiLoading(true)
    try {
      const res = await reportApi.generateAiSummary(viewing.id)
      setViewing((v) => (v ? { ...v, ai_summary: res.ai_summary } : v))
      message.success('AI 摘要已生成')
    } catch {
      // 错误提示已由请求层统一弹出
    } finally {
      setAiLoading(false)
    }
  }

  // 四期 L：失败分析（失败分类分布 + 失败步骤）
  const handleAnalysis = async () => {
    if (!viewing) return
    setAnalysisLoading(true)
    try {
      const res = await reportApi.getReportAnalysis(viewing.id)
      setAnalysis(res)
    } catch {
      // 错误提示已由请求层统一弹出
    } finally {
      setAnalysisLoading(false)
    }
  }

  // 四期 L：一键提单（禅道/Jira）；五期 #5：成功后回写字段直接更新报告详情
  const handleTicket = async () => {
    if (!viewing) return
    setTicketLoading(true)
    try {
      const res = await reportApi.createTicket(viewing.id)
      message.success(res.message || `提单成功${res.ticket_key ? `：${res.ticket_key}` : ''}`)
      // 后端回写报告 ticket 字段，前端同步刷新详情展示
      if (res.ticket_no) {
        setViewing((v) =>
          v
            ? {
                ...v,
                ticket_platform: res.ticket_platform || v.ticket_platform,
                ticket_no: res.ticket_no,
                ticket_url: res.ticket_url || v.ticket_url,
              }
            : v,
        )
      } else {
        // 兜底：响应未携带回写字段时重新拉详情
        const detail = await reportApi.getReport(viewing.id)
        setViewing(detail)
      }
    } catch {
      // 错误提示已由请求层统一弹出（如未配置提单平台）
    } finally {
      setTicketLoading(false)
    }
  }

  // 四期 K：开启/关闭分享
  const handleShare = async (disable = false) => {
    if (!viewing) return
    setShareLoading(true)
    try {
      if (disable) {
        await reportApi.disableShare(viewing.id)
        setViewing((v) => (v ? { ...v, share_token: '' } : v))
        message.success('分享已关闭')
      } else {
        const res = await reportApi.enableShare(viewing.id)
        setViewing((v) => (v ? { ...v, share_token: res.token } : v))
        // 复制只读分享链接（share 路由无需登录）
        const url = `${window.location.origin}/reports/share?token=${res.token}`
        try {
          await navigator.clipboard.writeText(url)
          message.success('分享已开启，链接已复制到剪贴板')
        } catch {
          message.info(`分享已开启：${url}`)
        }
      }
    } catch {
      // 错误提示已由请求层统一弹出
    } finally {
      setShareLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await reportApi.deleteReport(id)
      setPage(1)
      await loadReports(1, pageSize, keyword)
    } catch {
      // 错误已在拦截器处理
    }
  }

  const columns: ColumnsType<TestReport> = [
    {
      title: <div style={{ textAlign: 'center', fontSize: 14 }}>报告名称</div>,
      dataIndex: 'name',
      width: 180,
      render: (v: string, record: TestReport) => (
        <Button
          type="link"
          style={{ padding: 0, fontWeight: 500, whiteSpace: 'normal', textAlign: 'left', height: 'auto' }}
          onClick={() => handleView(record)}
        >
          {v || `报告 #${record.id}`}
        </Button>
      ),
    },
    {
      title: <div style={{ textAlign: 'center', fontSize: 14 }}>任务类型</div>,
      dataIndex: 'task_type',
      width: 110,
      render: (v: number) => <Tag color={taskTypeColor[v] || 'default'}>{taskTypeText[v] || '未知'}</Tag>,
    },
    {
      title: <div style={{ textAlign: 'center', fontSize: 14 }}>运行结果</div>,
      dataIndex: 'run_result',
      width: 90,
      render: (v: number) => (
        <Tag color={v === 1 ? 'success' : 'error'}>{runResultText[v] || '未知'}</Tag>
      ),
    },
    {
      title: <div style={{ textAlign: 'center', fontSize: 14 }}>运行模式</div>,
      dataIndex: 'run_mode',
      width: 90,
      render: (v: number) => <Tag>{runModeText[v] || '未知'}</Tag>,
    },
    {
      title: <div style={{ textAlign: 'center', fontSize: 14 }}>运行步骤数</div>,
      dataIndex: 'total_steps',
      width: 100,
      align: 'center',
      render: (v: number) => v ?? 0,
    },
    {
      title: <div style={{ textAlign: 'center', fontSize: 14 }}>成功步骤数</div>,
      dataIndex: 'success_steps',
      width: 100,
      align: 'center',
      render: (v: number) => <span style={{ color: '#10b981', fontWeight: 500 }}>{v ?? 0}</span>,
    },
    {
      title: <div style={{ textAlign: 'center', fontSize: 14 }}>通过率</div>,
      dataIndex: 'pass_rate',
      width: 130,
      render: (v: number) => {
        const rate = Number(v) || 0
        const color = rate >= 100 ? '#10b981' : rate >= 60 ? '#f59e0b' : '#ef4444'
        return (
          <Space size={6}>
            <Progress type="circle" size={26} percent={rate} strokeColor={color} showInfo={false} />
            <span style={{ color, fontWeight: 500 }}>{rate}%</span>
          </Space>
        )
      },
    },
    {
      title: <div style={{ textAlign: 'center', fontSize: 14 }}>运行耗时</div>,
      dataIndex: 'duration_ms',
      width: 100,
      render: formatDuration,
    },
    {
      title: <div style={{ textAlign: 'center', fontSize: 14 }}>运行时间</div>,
      dataIndex: 'run_time',
      width: 160,
      render: (v: string | null, record: TestReport) => {
        const t = v || record.create_time
        return t ? dayjs(t).format('YYYY-MM-DD HH:mm:ss') : <span style={{ color: 'var(--au-slate-muted)' }}>-</span>
      },
    },
    {
      title: <div style={{ textAlign: 'center', fontSize: 14 }}>执行人</div>,
      dataIndex: 'executor_name',
      width: 80,
      render: (v: string) => v || <span style={{ color: 'var(--au-slate-muted)' }}>-</span>,
    },
    {
      title: <div style={{ textAlign: 'center', fontSize: 14 }}>操作</div>,
      width: 120,
      render: (_: unknown, record: TestReport) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleView(record)} style={{ padding: '0 4px', height: 22 }}>
            查看
          </Button>
          <Popconfirm
            title="确定删除该测试报告？"
            onConfirm={() => { void handleDelete(record.id) }}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" size="small" danger style={{ padding: '0 4px', height: 22 }}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="aurora-page aurora-page--fit">
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          测试报告
        </Title>
        <Space>
          <Select
            placeholder="任务类型"
            allowClear
            style={{ width: 140 }}
            value={taskTypeFilter || undefined}
            onChange={(v) => {
              setTaskTypeFilter(v || 0)
              setPage(1)
            }}
            options={[
              { value: 1, label: '接口场景测试' },
              { value: 2, label: 'UI测试' },
            ]}
          />
          <Select
            placeholder="运行结果"
            allowClear
            style={{ width: 110 }}
            value={runResultFilter || undefined}
            onChange={(v) => {
              setRunResultFilter(v || 0)
              setPage(1)
            }}
            options={[
              { value: 1, label: '成功' },
              { value: 2, label: '失败' },
            ]}
          />
          <Select
            placeholder="运行模式"
            allowClear
            style={{ width: 110 }}
            value={runModeFilter || undefined}
            onChange={(v) => {
              setRunModeFilter(v || 0)
              setPage(1)
            }}
            options={[
              { value: 1, label: '同步' },
              { value: 2, label: '异步' },
              { value: 3, label: '定时器' },
            ]}
          />
          <Input
            allowClear
            placeholder="搜索报告名称"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 220 }}
          />
          <Button type="primary" onClick={handleSearch} style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)', color: '#fff' }}>
            查询
          </Button>
          <Button onClick={() => void loadReports()}>
            刷新
          </Button>
        </Space>
      </div>

      {/* 报告列表 */}
      <div className="content-card">
        <div className="content-card-header">
          <div className="content-card-title">
            <FileTextOutlined style={{ marginRight: 8, color: 'var(--au-primary)' }} />
            报告列表
            {/* 项目隔离标识：当前列表只展示该项目下的报告 */}
            <span style={{ marginLeft: 10, fontSize: 13, fontWeight: 400, color: 'var(--au-slate-muted)' }}>
              （项目：{currentProject?.name || '未选择'}）
            </span>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Table
            columns={columns}
            dataSource={reports}
            rowKey="id"
            loading={loading}
            size="middle"
            scroll={{ y: '100%' }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: (t) => `共 ${t} 条`,
              onChange: (p, ps) => {
                setPage(p)
                setPageSize(ps)
              },
            }}
          />
        </div>
      </div>

      {/* 报告详情抽屉：全宽样式，上方统计信息 + 下方步骤执行明细 */}
      <Drawer
        title="测试报告详情"
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        width="80%"
        rootClassName="aurora-drawer"
        rootStyle={{ top: 56, paddingBottom: 56 }}
        styles={{ body: { padding: '20px 24px' } }}
        destroyOnClose
      >
        {viewing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* 统计信息区：耗时统计 / 步骤统计 / 执行信息（白底卡片 + 柔和阴影） */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
              {/* 耗时统计 */}
              <div style={statCardStyle}>
                <div style={statCardTitleStyle}>耗时统计</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <StatRow label="总耗时">{formatDuration(viewing.duration_ms)}</StatRow>
                  <StatRow label={viewing.task_type === 2 ? '平均耗时（全部步骤）' : '平均耗时（只统计HTTP请求）'}>
                    {(() => {
                      const steps = (viewing.steps || []).filter((s) => (viewing.task_type === 2 ? true : s.step_type === 1))
                      if (steps.length === 0) return '-'
                      return formatDuration(Math.round(steps.reduce((acc, s) => acc + (s.duration_ms || 0), 0) / steps.length))
                    })()}
                  </StatRow>
                </div>
              </div>

              {/* 步骤统计 */}
              <div style={statCardStyle}>
                <div style={statCardTitleStyle}>步骤统计</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <StatRow label="成功率">
                    <span style={{ color: (Number(viewing.pass_rate) || 0) >= 100 ? 'var(--au-primary)' : '#c98d2b' }}>
                      {Number(viewing.pass_rate) || 0}%
                    </span>
                  </StatRow>
                  <StatRow label="成功 / 失败 / 错误 / 跳过">
                    <span style={{ color: 'var(--au-primary)' }}>{viewing.success_steps ?? 0}</span>
                    <span style={{ color: 'var(--au-slate-muted)' }}> / </span>
                    <span style={{ color: 'var(--au-terracotta)' }}>{viewing.failed_steps ?? 0}</span>
                    <span style={{ color: 'var(--au-slate-muted)' }}> / </span>
                    <span style={{ color: '#c98d2b' }}>{viewing.error_steps ?? 0}</span>
                    <span style={{ color: 'var(--au-slate-muted)' }}> / </span>
                    <span style={{ color: 'var(--au-slate-muted)' }}>{viewing.skipped_steps ?? 0}</span>
                  </StatRow>
                </div>
              </div>

              {/* 执行信息 */}
              <div style={statCardStyle}>
                <div style={statCardTitleStyle}>执行信息</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <StatRow label="执行时间">
                    {(viewing.run_time || viewing.create_time) ? dayjs(viewing.run_time || viewing.create_time).format('YYYY-MM-DD HH:mm:ss') : '-'}
                  </StatRow>
                  <StatRow label="操作人">{viewing.executor_name || '-'}</StatRow>
                </div>
              </div>
            </div>

            {/* 运行摘要（通过：主题绿提示条；有失败/错误步骤：红色提示条） */}
            {viewing.summary && (
              (() => {
                const hasFailure = (viewing.failed_steps ?? 0) > 0 || (viewing.error_steps ?? 0) > 0
                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '14px 18px',
                      background: hasFailure ? 'var(--au-terracotta-light)' : 'var(--au-primary-50)',
                      border: hasFailure ? '1px solid var(--au-terracotta)' : '1px solid var(--au-primary-100)',
                      borderRadius: 12,
                      color: hasFailure ? 'var(--au-terracotta)' : 'var(--au-primary-dark)',
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    {hasFailure ? (
                      <CloseCircleOutlined style={{ fontSize: 18, color: 'var(--au-terracotta)', flexShrink: 0 }} />
                    ) : (
                      <CheckCircleOutlined style={{ fontSize: 18, color: 'var(--au-primary)', flexShrink: 0 }} />
                    )}
                    <span>{viewing.summary}</span>
                  </div>
                )
              })()
            )}

            {/* 四期 K/L：智能分析与分享卡（AI 摘要 / 失败分析 / 一键提单 / 只读分享） */}
            <div style={{ ...statCardStyle, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={statCardTitleStyle}>智能分析与分享</div>
                <Space size={8}>
                  <Button size="small" loading={aiLoading} onClick={handleGenSummary}>
                    {viewing.ai_summary ? '重新生成 AI 摘要' : '生成 AI 摘要'}
                  </Button>
                  {(viewing.failed_steps ?? 0) > 0 || (viewing.error_steps ?? 0) > 0 ? (
                    <Button
                      size="small"
                      loading={analysisLoading}
                      onClick={() => (analysis ? setAnalysis(null) : handleAnalysis())}
                    >
                      {analysis ? '收起失败分析' : '失败分析'}
                    </Button>
                  ) : null}
                  {(viewing.failed_steps ?? 0) > 0 || (viewing.error_steps ?? 0) > 0 ? (
                    <Space size={6}>
                      {/* 五期 #5：提单回写展示（缺陷号可点击跳转禅道/Jira） */}
                      {viewing.ticket_no ? (
                        <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                          {viewing.ticket_url ? (
                            <a href={viewing.ticket_url} target="_blank" rel="noreferrer">
                              {viewing.ticket_platform === 'jira' ? 'JIRA' : '禅道'} #{viewing.ticket_no}
                            </a>
                          ) : (
                            `缺陷 #${viewing.ticket_no}`
                          )}
                        </Tag>
                      ) : null}
                      <Popconfirm
                        title={viewing.ticket_no ? '已提过单，确定再次提交新缺陷？' : '确定把该失败报告提交到禅道/Jira？'}
                        description="需先在「系统配置 → UI模块设置」中配置提单平台"
                        onConfirm={handleTicket}
                        okText="提单"
                        cancelText="取消"
                      >
                        <Button size="small" type="primary" ghost loading={ticketLoading}>
                          {viewing.ticket_no ? '重新提单' : '一键提单'}
                        </Button>
                      </Popconfirm>
                    </Space>
                  ) : null}
                  {viewing.share_token ? (
                    <Popconfirm title="确定关闭该报告的分享链接？" onConfirm={() => handleShare(true)} okText="确定" cancelText="取消" okButtonProps={{ danger: true }}>
                      <Button size="small" loading={shareLoading}>关闭分享</Button>
                    </Popconfirm>
                  ) : (
                    <Button size="small" loading={shareLoading} onClick={() => handleShare(false)}>分享报告</Button>
                  )}
                </Space>
              </div>
              {/* AI 摘要 */}
              {viewing.ai_summary ? (
                <div
                  style={{
                    padding: '12px 14px',
                    borderRadius: 10,
                    background: 'var(--au-primary-50)',
                    border: '1px solid var(--au-primary-100)',
                    fontSize: 13,
                    lineHeight: '22px',
                    color: 'var(--au-slate)',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {viewing.ai_summary}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>
                  暂无 AI 摘要。点击「生成 AI 摘要」调用平台已启用的 LLM 生成执行摘要（概括结果、关键失败点与建议）。
                </div>
              )}
              {/* 失败分析：分类分布 + 失败步骤明细 */}
              {analysis && (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>失败分类：</span>
                    {Object.keys(analysis.distribution).length === 0 ? (
                      <span style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>无</span>
                    ) : (
                      Object.entries(analysis.distribution).map(([cat, count]) => (
                        <Tag key={cat} color={cat === '其他' ? 'default' : 'orange'}>
                          {cat} × {count}
                        </Tag>
                      ))
                    )}
                  </div>
                  {(analysis.failed_steps || []).length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {analysis.failed_steps.map((s, i) => (
                        <div
                          key={s.step_id || i}
                          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--au-border)', fontSize: 13, background: 'var(--au-header-bg)' }}
                        >
                          <span style={{ fontWeight: 600, color: 'var(--au-terracotta)' }}>{s.step_name}</span>
                          {s.error && (
                            <div style={{ fontSize: 12, color: 'var(--au-slate-muted)', marginTop: 2, wordBreak: 'break-all' }}>
                              {s.error}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 步骤执行明细（白底卡片 + 衬线标题） */}
            <div
              style={{
                borderRadius: 12,
                background: 'var(--au-surface)',
                border: '1px solid var(--au-border)',
                boxShadow: 'var(--au-shadow-soft)',
              }}
            >
              <div
                style={{
                  fontFamily: "'Noto Serif SC', serif",
                  fontSize: 16,
                  fontWeight: 700,
                  color: 'var(--au-primary-dark)',
                  padding: '14px 16px 10px',
                }}
              >
                步骤执行明细（共 {viewing.steps?.length ?? 0} 条）
              </div>
              <Table
                size="small"
                rowKey="id"
                dataSource={viewing.steps || []}
                pagination={false}
                rowClassName={(record: TestReportStep) => (record.depth > 0 ? 'report-step-child-row' : '')}
                columns={[
                  {
                    title: <div style={{ textAlign: 'center', fontSize: 13 }}>#</div>,
                    width: 56,
                    align: 'center' as const,
                    render: (_: unknown, __: TestReportStep, idx: number) => idx + 1,
                  },
                  {
                    title: <div style={{ textAlign: 'center', fontSize: 13 }}>步骤名称</div>,
                    dataIndex: 'step_name',
                    render: (v: string, record: TestReportStep) => (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          paddingLeft: record.depth > 0 ? 20 : 0,
                          fontWeight: record.depth > 0 ? 400 : 600,
                        }}
                      >
                        {record.depth > 0 ? (
                          '└ '
                        ) : (
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              flexShrink: 0,
                              background:
                                record.status === 2 || record.status === 3
                                  ? 'var(--au-terracotta)'
                                  : record.status === 4
                                    ? 'var(--au-slate-muted)'
                                    : 'var(--au-primary)',
                            }}
                          />
                        )}
                        {v || '-'}
                      </span>
                    ),
                  },
                  {
                    title: <div style={{ textAlign: 'center', fontSize: 13 }}>步骤类型</div>,
                    dataIndex: 'step_type',
                    width: 110,
                    render: (v: number) => <Tag>{stepTypeText[v] || '未知'}</Tag>,
                  },
                  {
                    title: <div style={{ textAlign: 'center', fontSize: 13 }}>状态</div>,
                    dataIndex: 'status',
                    width: 90,
                    render: (v: number) => (
                      <Tag color={reportStepStatusColor[v] || 'default'}>{reportStepStatusText[v] || '未知'}</Tag>
                    ),
                  },
                  {
                    title: <div style={{ textAlign: 'center', fontSize: 13 }}>耗时</div>,
                    dataIndex: 'duration_ms',
                    width: 100,
                    render: formatDuration,
                  },
                ]}
                expandable={{
                  expandedRowRender: (record: TestReportStep) =>
                    viewing.task_type === 2 ? (
                      <UIStepResultDetail record={record} isDark={isDark} />
                    ) : (
                      <StepResultDetail record={record} isDark={isDark} />
                    ),
                  rowExpandable: () => true,
                }}
              />
            </div>

            {/* 五期 #3：执行产物直达（UI 报告关联执行时展示，截图预览 / trace 在线回放 / 产物下载） */}
            {(viewing.execution_artifacts?.length ?? 0) > 0 && (
              <div style={{ ...statCardStyle, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={statCardTitleStyle}>执行产物</div>
                  {viewing.execution_id ? <Tag>执行 #{viewing.execution_id}</Tag> : null}
                </div>
                <Image.PreviewGroup>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                    {viewing.execution_artifacts!.map((a) => {
                      const ext = a.name.split('.').pop()?.toLowerCase() || ''
                      const isImg = ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)
                      const isZip = ext === 'zip'
                      const sizeText = a.size >= 1024 * 1024 ? `${(a.size / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(a.size / 1024))}KB`
                      return (
                        <div
                          key={a.url}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: 8,
                            borderRadius: 8, border: '1px solid var(--au-border)', fontSize: 12,
                          }}
                        >
                          {isImg ? (
                            <Image src={a.url} alt={a.name} width={96} height={60} style={{ objectFit: 'cover', borderRadius: 6 }} />
                          ) : null}
                          {isZip ? (
                            <a href={`/trace-viewer/index.html?trace=${encodeURIComponent(a.url)}`} target="_blank" rel="noreferrer">
                              {a.name} · 在线回放
                            </a>
                          ) : (
                            <a href={a.url} target="_blank" rel="noreferrer">{a.name}</a>
                          )}
                          <span style={{ color: 'var(--au-slate-muted)' }}>{sizeText}</span>
                        </div>
                      )
                    })}
                  </div>
                </Image.PreviewGroup>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  )
}
